import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchGameChangelog } from '../../api/games';

interface Props {
  open: boolean;
  gameId: string | null;
  gameName: string;
  onClose: () => void;
}

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ChangelogDialog({ open, gameId, gameName, onClose }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['game-changelog', gameId],
    queryFn: () => fetchGameChangelog(gameId!),
    enabled: open && gameId !== null,
    staleTime: 30_000,
  });

  const entries = data?.news ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Changelog — {gameName}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Keine Changelog-Einträge verfügbar.
          </Typography>
        )}
        {!isLoading && entries.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {entries.map((e, i) => (
              <Box key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{e.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtDate(e.date)}
                </Typography>
                <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  <Typography variant="body2" sx={{ maxWidth: 600 }}>
                    {e.contents}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="primary"
                  endIcon={<CircularProgress size={14} />}
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 0.5, fontSize: 12 }}
                >
                  Read more on Steam →
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
